/**
 * Plain words (docs/SPEC.md §5.7, P28-T15): the system's words, which nothing
 * a model writes may show the person. The same list as
 * `backend/copy/plain.py::SYSTEM_WORDS`, in the same order and read the same
 * way (`tests/backend/copy/test_plain.py` keeps the two equal): a word without
 * case and with its plural; a word ending in a colon only before what it names
 * (`photo:1`); a scene id ("s1" … "s12") only in lower case. "Stand-in"
 * (hyphenated) is a word the person reads; so are "person", "people", "crowd"
 * and "figure" alone, while `figure:`, "skeleton" and "clip" are the system's
 * (P29). A grip's name (`grip_r`, found as the prefix `grip_`), `two_hand`
 * and the clips' ids (`point-remote`, …) are the system's too; "hold", "hand",
 * "remote" and "screen" are plain (P30).
 */

export const SYSTEM_WORDS: readonly string[] = [
  "the person",
  "the user",
  "the customer",
  "catalogue",
  "catalog",
  "upload id",
  "upload:",
  "photo:",
  "site:",
  "objects:",
  "import:",
  "design:",
  "part:",
  "figure:",
  "standin",
  "stand_in",
  "brief.json",
  "film.json",
  "schema",
  "cast member",
  "slot",
  "rig",
  "skeleton",
  "clip",
  "grip_",
  "two_hand",
  "point-remote",
  "show-camera",
  "hold-chest",
  "look-phone",
  "hand-over",
  "grip-power",
  "$primary",
  "$accent",
  "s1",
  "s2",
  "s3",
  "s4",
  "s5",
  "s6",
  "s7",
  "s8",
  "s9",
  "s10",
  "s11",
  "s12",
];

const escape = (word: string): string =>
  word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

function pattern(word: string): RegExp {
  const body = escape(word);
  if (word.endsWith(":") || word.endsWith("_"))
    return new RegExp(`(?<![\\w$])${body}(?=[\\w@-])`, "i");
  if (/^s\d{1,2}$/.test(word))
    return new RegExp(`(?<![\\w$.-])${body}(?![\\w-])`);
  return new RegExp(`(?<![\\w$])${body}(?:e?s)?(?!\\w)`, "i");
}

const PATTERNS: readonly [string, RegExp][] = SYSTEM_WORDS.map((w) => [
  w,
  pattern(w),
]);

/** The system words `text` holds, each once, in the list's order. */
export function systemWords(text: string | null | undefined): string[] {
  if (!text) return [];
  return PATTERNS.filter(([, p]) => p.test(text)).map(([w]) => w);
}

/** Whether `text` holds no system word. */
export function isPlain(text: string | null | undefined): boolean {
  return systemWords(text).length === 0;
}

// ---------------------------------------------------- references in plain words

/** A reference a model may write where the person reads it — a brief scene's
 *  `what` names the person's photo as `photo:<n>` (`SYSTEM.md` rule 17) — or
 *  a picture's own name (`site-logo`), with the article written before it. */
const REF =
  /(?:(?<![\w$])(the|a|an)\s+)?(?<![\w$])(?:(photo|upload|site|objects|import|design|part|figure|url):([^\s,;()]+)|(site-logo|brand-logo)(?![\w-]))/gi;
/** What ends a sentence rather than a reference (`… on photo:2.`). */
const TRAIL = /[.:!?\]"'”’]+$/;
const NUMBER = /^\d{1,3}$/;
/** Words that carry their own article: one written before the reference goes. */
const OWN_ARTICLE = /^(?:your|a|an) /i;
const LOGOS: Record<string, string> = {
  "site-logo": "your site's logo",
  "brand-logo": "your brand kit's logo",
};
const OTHERS: Record<string, string> = {
  upload: "your picture",
  import: "your imported object",
  design: "your design",
  part: "a stand-in",
};

const base = (ref: string): string => ref.split("@", 1)[0];

function refWords(
  kind: string,
  value: string,
  names: Record<string, string>,
): string {
  const ref = `${kind}:${value}`;
  const named =
    names[ref] ??
    Object.entries(names).find(([key]) => base(key) === base(ref))?.[1];
  if (named) return named;
  if (kind === "photo")
    return NUMBER.test(value) ? `your photo ${value}` : "your photo";
  if (kind === "site")
    return NUMBER.test(value) ? `picture ${value} from your site` : "your site";
  if (kind === "url") return value;
  if (kind === "objects" || kind === "figure")
    return (
      base(value).split(/[-_]+/).filter(Boolean).join(" ") ||
      (kind === "objects" ? "an object" : "a figure")
    );
  return OTHERS[kind];
}

/** One reference of a text and the words the card shows for it. */
export interface RefShown {
  /** The reference as the model wrote it, its article included. */
  ref: string;
  /** What the person reads instead. */
  words: string;
}

/** `text` with each reference in plain words (P28-T16, the brief card's
 *  `what`), and each reference with its words: `photo:2` "your photo 2",
 *  `site:1` "picture 1 from your site", `upload:<id>` "your picture",
 *  `objects:<id>[@<v>]` the Object's name (`names`, by the reference with or
 *  without its version; else the id's words), `figure:<id>[@<v>]` the figure's
 *  name the same way (P29), `import:`, `design:`, `part:`
 *  their name or "your imported object", "your design", "a stand-in",
 *  `url:<address>` the address, `site-logo` and `brand-logo` "your site's
 *  logo", "your brand kit's logo"; an article before a reference whose words
 *  carry their own goes. The same reading as
 *  `backend/copy/plain.py::plain_refs` (the shared cases `refs.cases.json`). */
export function readRefs(
  text: string | null | undefined,
  names: Record<string, string> = {},
): { text: string; refs: RefShown[] } {
  const refs: RefShown[] = [];
  if (!text) return { text: "", refs };
  const out = text.replace(
    REF,
    (
      whole: string,
      article: string | undefined,
      kind: string | undefined,
      raw: string | undefined,
      logo: string | undefined,
      offset: number,
    ) => {
      let end = "";
      let words: string;
      if (logo) {
        words = LOGOS[logo.toLowerCase()];
      } else {
        const value0 = raw ?? "";
        end = TRAIL.exec(value0)?.[0] ?? "";
        const value = value0.slice(0, value0.length - end.length);
        if (!value || !kind) return whole;
        words = refWords(kind.toLowerCase(), value, names);
      }
      const ref = whole.slice(0, whole.length - end.length);
      let shown: string;
      if (article && !OWN_ARTICLE.test(words)) {
        shown = `${article} ${words}`;
      } else {
        // it begins the sentence
        if (
          (article && article[0] !== article[0].toLowerCase()) ||
          offset === 0
        )
          words = words[0].toUpperCase() + words.slice(1);
        shown = words;
      }
      refs.push({ ref, words: shown });
      return shown + end;
    },
  );
  return { text: out, refs };
}

/** `text` with each reference in plain words (`readRefs`). */
export function plainRefs(
  text: string | null | undefined,
  names: Record<string, string> = {},
): string {
  return readRefs(text, names).text;
}

/** What the person edited, in the brief's own words again: the words of each
 *  reference (`readRefs` of the text they started from) become that reference,
 *  in order, so that saving a line changes only what they changed; "your
 *  photo <n>" is `photo:<n>` wherever it is written. */
export function refsBack(edited: string, refs: readonly RefShown[]): string {
  let out = edited;
  const byWords = new Map<string, string[]>();
  for (const { ref, words } of refs) {
    byWords.set(words, [...(byWords.get(words) ?? []), ref]);
  }
  const longest = [...byWords.keys()].sort((a, b) => b.length - a.length);
  for (const words of longest) {
    const queue = [...(byWords.get(words) ?? [])];
    let at = 0;
    for (;;) {
      const found = out.indexOf(words, at);
      if (found < 0) break;
      const ref = queue.length > 1 ? (queue.shift() as string) : queue[0];
      out = out.slice(0, found) + ref + out.slice(found + words.length);
      at = found + ref.length;
    }
  }
  return out.replace(/(?<![\w$:])your photo (\d{1,3})(?!\d)/gi, "photo:$1");
}
